import { r as createLogger } from "../logger-CdXeOlrX.js";
import { selectAlignmentMatrix, selectGpsPositions, selectOdometryPositions, selectOdometryRotations, selectZeroReference } from "./app-selectors.js";
import { t as buildMapData } from "../map-data-CDJWmEnd.js";
import { subscribeToSelector } from "./subscribe-to-selector.js";
import { mat4, vec3 } from "gl-matrix";
import { calcGpsCoords } from "gps-plus-slam-js";
//#region ../src/state/store-subscribers.ts
const log = createLogger("StoreSubscribers");
/**
* Wire store state-change subscriptions to visualization dependencies.
*
* Uses subscribeToSelector to decompose the monolithic subscriber into
* focused, change-gated callbacks:
* 1. Alignment matrix changes → apply matrix + capture snapshots
* 2. GPS position changes → incremental event visualization + map overlay
* 3. Reference point changes → forward to map overlay
*
* Each subscription uses reference equality to detect changes — callbacks
* only fire when the selector output actually differs between dispatches.
*
* Each call creates **fresh subscriptions** — no manual reset needed between
* sessions. Call the returned unsubscribe function to tear down all.
*
* @param store — any object satisfying SubscribableStore (RecorderStore, replay store, mock)
* @param deps  — visualization dependencies (real singletons or test mocks)
* @returns An unsubscribe function that removes all listeners from the store.
*/
function wireStoreSubscribers(store, deps) {
	const _odomVec = vec3.create();
	const _alignedVec = vec3.create();
	const unsubs = [];
	const alignmentSnapshotGps = [];
	/**
	* Rebuild the full map snapshot from the current store state and hand it to
	* the overlay's `render`. Called whenever the alignment matrix or the GPS
	* positions change — the change-gated subscriptions below provide the
	* throttling, so no extra debouncing is needed. The fused path is always
	* recomputed from the latest matrix inside `buildMapData` (D2).
	*
	* Returns the rendered `MapData` so the GPS subscription can center the map
	* on the SAME coordinate the dot shows (fused when available — 2026-07-06
	* fused-dot feedback, Slice B), or `null` when there is no overlay or it
	* has no `render` (the minimal `setGpsPosition`-only dep shape).
	*/
	let lastGpsPositionsRef = null;
	let lastOdomPositionsRef = null;
	let lastMatrixRef = null;
	let lastZeroRef = null;
	let lastSnapshotCount = -1;
	let lastMapData = null;
	function rebuildMap() {
		if (!deps.mapOverlay?.render) return null;
		const state = store.getState();
		const gpsPositions = selectGpsPositions(state);
		const odometryPositions = selectOdometryPositions(state);
		const alignmentMatrix = selectAlignmentMatrix(state);
		const zeroRef = selectZeroReference(state);
		if (gpsPositions === lastGpsPositionsRef && odometryPositions === lastOdomPositionsRef && alignmentMatrix === lastMatrixRef && zeroRef === lastZeroRef && alignmentSnapshotGps.length === lastSnapshotCount) return lastMapData;
		const data = buildMapData({
			rawGpsPath: gpsPositions.map((p) => ({
				lat: p.latitude,
				lng: p.longitude,
				accuracy: p.latLongAccuracy
			})),
			odometryPositions,
			odometryRotations: selectOdometryRotations(state),
			alignmentMatrix,
			zeroRef,
			alignmentSnapshots: alignmentSnapshotGps
		});
		lastGpsPositionsRef = gpsPositions;
		lastOdomPositionsRef = odometryPositions;
		lastMatrixRef = alignmentMatrix;
		lastZeroRef = zeroRef;
		lastSnapshotCount = alignmentSnapshotGps.length;
		lastMapData = data;
		try {
			deps.mapOverlay.render(data);
		} catch (err) {
			log.warn("Map overlay render failed; continuing with centering:", err);
		}
		return data;
	}
	unsubs.push(subscribeToSelector(store, selectAlignmentMatrix, (matrix) => {
		if (!matrix) return;
		deps.applyAlignmentMatrix(matrix);
		const state = store.getState();
		const odomPositions = selectOdometryPositions(state);
		if (odomPositions.length > 0) {
			const latestOdom = odomPositions[odomPositions.length - 1];
			const glMat = mat4.fromValues(...matrix);
			vec3.set(_odomVec, latestOdom[0], latestOdom[1], latestOdom[2]);
			vec3.transformMat4(_alignedVec, _odomVec, glMat);
			const snapshotPos = [
				_alignedVec[0],
				_alignedVec[1],
				_alignedVec[2]
			];
			deps.gpsEventVisualizer.addAlignmentSnapshot(snapshotPos);
			deps.onAlignmentSnapshot?.(snapshotPos);
			const zeroRef = selectZeroReference(state);
			if (zeroRef) {
				const gps = calcGpsCoords(zeroRef, _alignedVec);
				alignmentSnapshotGps.push({
					lat: gps.lat,
					lng: gps.lon
				});
			}
		}
		rebuildMap();
	}));
	unsubs.push(subscribeToSelector(store, selectGpsPositions, (gpsPositions, prevPositions) => {
		const prevCount = prevPositions?.length ?? 0;
		const state = store.getState();
		const zeroRef = selectZeroReference(state);
		if (zeroRef && !deps.gpsEventVisualizer.getZeroRef()) deps.gpsEventVisualizer.setZeroRef(zeroRef);
		const odomPositions = selectOdometryPositions(state);
		const odomRotations = selectOdometryRotations(state);
		for (let i = prevCount; i < gpsPositions.length; i++) {
			const gpsPoint = gpsPositions[i];
			const odomPos = odomPositions[i];
			if (gpsPoint && odomPos) {
				const accuracy = deps.showAccuracySpheres ? {
					horizontal: gpsPoint.latLongAccuracy,
					vertical: gpsPoint.altitudeAccuracy
				} : void 0;
				deps.gpsEventVisualizer.addGpsEvent(gpsPoint.coordinates, odomPos, accuracy);
				deps.onNewGpsPosition?.(gpsPoint.coordinates);
				deps.onNewGpsLatLng?.(gpsPoint.latitude, gpsPoint.longitude);
				const odomRot = odomRotations[i];
				if (odomRot) deps.onNewOdomPose?.(odomPos, odomRot);
			}
		}
		const mapData = rebuildMap();
		if (deps.mapOverlay && gpsPositions.length > 0) if (mapData?.userPosition) deps.mapOverlay.setGpsPosition(mapData.userPosition.lat, mapData.userPosition.lng);
		else {
			const lastGpsPoint = gpsPositions[gpsPositions.length - 1];
			deps.mapOverlay.setGpsPosition(lastGpsPoint.latitude, lastGpsPoint.longitude);
		}
	}));
	return () => {
		for (const unsub of unsubs) unsub();
	};
}
//#endregion
export { wireStoreSubscribers };
